import { DateTime } from 'luxon';
import { EMpanAssignmentMode, EMpanEditMode, MeterPointDetails } from '../types';
import { useStandingData } from './useStandingData';
import * as Yup from 'yup';

export const useMeterPointDetailsFormValidationSchema = (mode: EMpanAssignmentMode, contractStartDate?: DateTime, editMode?: EMpanEditMode) => {
  const baseSchema = useBaseMeterPointDetailsValidationSchema();

  return Yup.object<MeterPointDetails, { [Key in keyof MeterPointDetails]: Yup.AnySchema<MeterPointDetails[Key]> }>({
    ...baseSchema,
    contractId: Yup.string().required('Field is required'),
    effectiveDate: Yup.string()
      .required('Field is required')
      .test('is-after-contract-start', `Effective date cannot be before the contract start date ${contractStartDate && contractStartDate.toFormat('dd/MM/yyyy')}`, (effectiveDate: string) => {
        if (!contractStartDate) return true;
        return DateTime.fromISO(effectiveDate) >= contractStartDate;
      }),
    contractSiteStatusId: mode === EMpanAssignmentMode.EDIT ? Yup.string().required('Field is required') : Yup.string().optional(),
    transferCustomerGroupId: editMode === EMpanEditMode.TRANSFER ? Yup.string().required('Field is required') : Yup.string().optional(),
    transferContractId: editMode === EMpanEditMode.TRANSFER ? Yup.string().required('Field is required') : Yup.string().optional(),
    transferDate:
      editMode === EMpanEditMode.TRANSFER
        ? Yup.string()
            .required('Field is required')
            .when(['transferContractStartDate'], ([transferContractStartDate]: string[], schema) => {
              if (!transferContractStartDate) return schema;
              const startDatetime = DateTime.fromISO(transferContractStartDate).toUTC(undefined, { keepLocalTime: true }).startOf('day');
              return schema.test('is-after-contract-start', `Transfer date cannot be before the contract start date ${startDatetime.toFormat('dd/MM/yyyy')}`, (selectedTransferDate: string) => {
                return DateTime.fromISO(selectedTransferDate) >= startDatetime;
              });
            })
        : Yup.string().optional()
  });
};

export const useBulkAssignFormValidationSchema = () => {
  const baseSchema = useBaseMeterPointDetailsValidationSchema();
  return Yup.object<MeterPointDetails, { [Key in keyof MeterPointDetails]: Yup.AnySchema<MeterPointDetails[Key]> }>(baseSchema);
};

const useBaseMeterPointDetailsValidationSchema = () => {
  const {
    standingData: { standingDataMeterWorkRequiredList, standingDataAssetOwnershipList, standingDataSiteTypeList, standingDataNoCommsList }
  } = useStandingData();

  return {
    mprn: Yup.string().required(),
    effectiveDate: Yup.string().required(),
    siteReferenceName: Yup.string()
      .optional()
      .matches(/^[\w\W]{0,500}$/, 'Please ensure you have entered a site reference name no greater than 500 characters'),
    isImport: Yup.string().oneOf(['Y', 'N']).required(),
    isExport: Yup.string().oneOf(['Y', 'N']).required(),
    siteTypeId: Yup.string()
      .optional()
      .oneOf(standingDataSiteTypeList.map(item => item.value)),
    assetOwnership: Yup.string()
      .optional()
      .oneOf(standingDataAssetOwnershipList.map(item => item.value)),
    leasePurchaseTerm: Yup.string()
      .optional()
      .matches(/^\d{0,3}$/, 'Please ensure you have entered a lease purchase term no greater than 999 years'),
    relatedMprn: Yup.string()
      .optional()
      .matches(/^$|^[0-9]{10,13}$/, 'Please ensure you have entered a valid length MPRN between 10 and 13 digits'),
    batchName: Yup.string()
      .optional()
      .matches(/^[\w\W]{0,100}$/, 'Please ensure you have entered a batch name no greater than 100 characters'),
    meterWorkRequired: Yup.string()
      .optional()
      .oneOf(standingDataMeterWorkRequiredList.map(item => item.value)),
    parkingNotes: Yup.string()
      .optional()
      .matches(/^[\w\W]{0,200}$/, 'Please ensure you have entered parking notes no greater than 200 characters'),
    meterLocationNotes: Yup.string()
      .optional()
      .matches(/^[\w\W]{0,200}$/, 'Please ensure you have entered meter location notes no greater than 200 characters'),
    noCommsId: Yup.string()
      .optional()
      .oneOf(standingDataNoCommsList.map(item => item.value)),
    custOwnedComms: Yup.string<'Y' | 'N'>().optional().oneOf(['Y', 'N']),
    fuseSize: Yup.string()
      .optional()
      .matches(/^[\w\W]{0,20}$/, 'Please ensure you have entered fuse size no greater than 20 characters'),

    contact: Yup.object({
      siteContactTitle: Yup.string()
        .required()
        .matches(/^[\w\W]{2,50}$/, 'Please ensure you have entered a title between 2 and 50 characters'),
      siteContactFirstname: Yup.string()
        .optional()
        .matches(/^[\w\W]{0,50}$/, 'Please ensure you have entered a first name no greater than 50 characters'),
      siteContactSurname: Yup.string()
        .required()
        .matches(/^[\w\W]{1,50}$/, 'Please ensure you have entered a surname between 1 and 50 characters'),
      siteContactNumber: Yup.string()
        .required()
        .matches(/^0[12378]\d{8}\d?$/, 'Please ensure you have entered a valid contact number'),
      siteContactExtensionNumber: Yup.string()
        .optional()
        .matches(/^\d{0,10}$/, 'Please ensure you have entered an extension number no greater than 10 characters'),
      emailAddress: Yup.string()
        .optional()
        .matches(/^$|^(?=.{0,50}$)\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/, 'Please ensure you have entered a valid email address'),
      mobileNumber: Yup.string()
        .optional()
        .matches(/^$|^0[12378]\d{8}\d?$/, 'Please ensure you have entered a valid contact number'),
      faxNumber: Yup.string()
        .optional()
        .matches(/^$|^[\w\W]{0,50}$/, 'Please ensure you have entered a fax number no greater than 50 characters')
    }),
    address: Yup.object({
      addressName: Yup.string()
        .required()
        .matches(/^[\w\W]{1,50}$/, 'Please ensure you have entered an address name between 1 and 50 characters'),
      addressLine1: Yup.string()
        .required()
        .matches(/^[\w\W]{1,50}$/, 'Please ensure you have entered an address line 1 between 1 and 50 characters'),
      addressLine2: Yup.string()
        .optional()
        .matches(/^[\w\W]{0,50}$/, 'Please ensure you have entered an address line 2 no greater than 50 characters'),
      addressLine3: Yup.string()
        .optional()
        .matches(/^[\w\W]{0,50}$/, 'Please ensure you have entered an address line 3 no greater than 50 characters'),
      town: Yup.string()
        .required()
        .matches(/^[\w\W]{1,50}$/, 'Please ensure you have entered a town between 1 and 50 characters'),
      county: Yup.string()
        .required()
        .matches(/^[\w\W]{1,50}$/, 'Please ensure you have entered a county between 1 and 50 characters'),
      postcode: Yup.string()
        .required('postcode is required')
        .matches(/^[a-zA-z]{1,2}[0-9][0-9A-Za-z]?\s?[0-9][a-zA-z]{2}$/, 'Invalid postcode')
    }).required()
  };
};
